import React from 'react';
import { Box, BoxProps, Chip, ChipProps, Tooltip, Avatar } from '@mui/material';
import { SparkEllipsisTypography, SparkImageLoader } from 'sharedComponents/UI';
import { SPARK_BADGE_TYPES } from 'constants/common';
import type { SparkBadge } from 'types/common/SparkBadge';

type SparkBadgesProps = {
  badges: SparkBadge[];
  containerStyles?: BoxProps['sx'];
  chipLabelStyles?: React.CSSProperties;
  chipStyles?: ChipProps['sx'];
  imageSize?: string | number;
};

const SparkBadges: React.FC<SparkBadgesProps> = ({
  badges,
  containerStyles,
  chipStyles = {},
  chipLabelStyles = {},
  imageSize = 24,
}) => {
  const sortedBadges = [...badges].sort((a, b) => a.priority - b.priority);

  return (
    <Box display='flex' gap={1} width='100%' sx={containerStyles}>
      {sortedBadges.map((badge, index) => {
        const isCreatorBadge = badge.type === SPARK_BADGE_TYPES.Creator;
        
        return (
          <Tooltip
            title={badge.toolTip}
            placement='top'
            key={`${badge.text}-${index}`}
          >
            <Chip
              label={
                <Box display='flex' alignItems='center' gap={1}>
                  {!!badge.iconURL && (
                    <>
                      {isCreatorBadge ? (
                        <Avatar
                          src={badge.iconURL || ''}
                          alt={badge.text || ''}
                          sx={{
                            width: imageSize,
                            height: imageSize,
                            '& img': {
                              objectFit: 'contain',
                              padding: '2px',
                            }
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: imageSize,
                            height: imageSize,
                            overflow: 'hidden',
                          }}
                        >
                          <SparkImageLoader
                            src={badge.iconURL || ''}
                            alt={badge.iconURL || ''}
                            imageStyle={{ width: imageSize, height: imageSize }}
                          />
                        </Box>
                      )}
                    </>
                  )}
                  <SparkEllipsisTypography
                    variant='body2'
                    sx={{
                      WebkitLineClamp: 1,
                      textWrap: 'pretty',
                      wordBreak: 'break-all',
                    }}
                  >
                    {badge.text}
                  </SparkEllipsisTypography>
                </Box>
              }
              sx={{
                backgroundColor: badge.fillColor || 'transparent',
                color: badge.textColor,
                borderColor: badge.borderColor,
                borderRadius: '30px',
                ...chipStyles,
                '& .MuiChip-label': {
                  padding: badge.fillColor ? 2 : 0,
                  ...chipLabelStyles,
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default SparkBadges;
